import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GetActivityAnalyticsResponseData } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { AnalyticsQueryFilter } from '../util/analytics-query-filter';
import { FETCH_USER_ACTIVITY_QUERY_KEY } from './analytics-query-keys';

export type FetchTasksAndAlertsUserActivityResult = {
    data: GetActivityAnalyticsResponseData | undefined;
    status: 'error' | 'success' | 'pending';
    refetch: () => void;
};

/** Tasks and alerts totalled per user over the filtered range. */
export function useFetchTasksAndAlertsUserActivity(
    query: AnalyticsQueryFilter
): FetchTasksAndAlertsUserActivityResult {
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const { status, data, refetch } = useQuery({
        queryKey: [FETCH_USER_ACTIVITY_QUERY_KEY, query],
        queryFn: () => {
            return openApiRef.current.analytics
                .getUserActivity(query)
                .then((response) => response.data.data);
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        staleTime: 0
    });

    return {
        status,
        data,
        refetch: () => {
            void refetch();
        }
    };
}
